import type { ElevationPoint } from '../types';

/**
 * Aggregate elevation data to a uniform number of points for optimal chart performance.
 * This creates evenly spaced points along the distance and interpolates elevation/steepness.
 */
export const aggregateElevationData = (data: ElevationPoint[], numPoints = 150): ElevationPoint[] => {
  if (data.length === 0) return [];
  if (data.length <= numPoints) return data; // No need to aggregate if already small
  
  const rangeStart = data[0].distance;
  const rangeEnd = data[data.length - 1].distance;
  const rangeLength = rangeEnd - rangeStart;
  
  if (rangeLength === 0) return [data[0]];
  
  const uniformData: ElevationPoint[] = [];
  
  // Generate uniformly spaced points
  for (let i = 0; i < numPoints; i++) {
    const targetDist = rangeStart + (i / (numPoints - 1)) * rangeLength;
    
    // Find the interval in the original data array
    let j = 0;
    while (j < data.length - 1 && data[j + 1].distance < targetDist) j++;
    const p1 = data[j];
    const p2 = data[Math.min(j + 1, data.length - 1)];
    
    if (p1.distance === p2.distance) {
      uniformData.push({ ...p1 });
    } else {
      const t = (targetDist - p1.distance) / (p2.distance - p1.distance);
      uniformData.push({
        distance: targetDist,
        elevation: p1.elevation + t * (p2.elevation - p1.elevation),
        steepness: 0, // Will be calculated after all points are generated
      });
    }
  }
  
  // Recalculate steepness based on the actual aggregated points
  for (let i = 0; i < uniformData.length; i++) {
    if (i === 0 || i === uniformData.length - 1) {
      // For first and last point, use adjacent point
      if (uniformData.length === 1) {
        uniformData[i].steepness = 0;
      } else if (i === 0) {
        const distDiff = uniformData[1].distance - uniformData[0].distance;
        const elevDiff = uniformData[1].elevation - uniformData[0].elevation;
        uniformData[i].steepness = distDiff > 0 ? (elevDiff / (distDiff * 1000)) * 100 : 0;
      } else {
        const distDiff = uniformData[i].distance - uniformData[i - 1].distance;
        const elevDiff = uniformData[i].elevation - uniformData[i - 1].elevation;
        uniformData[i].steepness = distDiff > 0 ? (elevDiff / (distDiff * 1000)) * 100 : 0;
      }
    } else {
      // For middle points, calculate from previous to next point for smoother gradient
      const distDiff = uniformData[i + 1].distance - uniformData[i - 1].distance;
      const elevDiff = uniformData[i + 1].elevation - uniformData[i - 1].elevation;
      uniformData[i].steepness = distDiff > 0 ? (elevDiff / (distDiff * 1000)) * 100 : 0;
    }
  }
  
  return uniformData;
};
